import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useCopy } from "../context/CopyContext";
import { useBrand } from "../context/BrandContext";

interface Category {
  id: string;
  name: string;
  subtitle?: string;
  image: string;
}

interface CategorySelectorProps {
  onCategorySelect: (category: Category) => void;
  selectedCategoryId?: string;
  onScrollDirectionChange?: (direction: 'left' | 'right') => void;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  onCategorySelect,
  selectedCategoryId,
  onScrollDirectionChange,
}) => {
  const { getCopy } = useCopy();
  const { brandConfig, isDarkMode } = useBrand();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("plasticMetalPaperJar");
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollLeftRef = useRef<number>(0);
  
  // Get categories from copy
  const categoriesData = getCopy('emptyAccepted.categories');
  const categories: Category[] = Object.entries(categoriesData).map(([id, data]: [string, any]) => ({
    id,
    name: data.name,
    subtitle: data.subtitle,
    image: data.image,
  }));

  // Scroll handler with direction detection and snap-to-center
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    
    const currentScrollLeft = scrollContainerRef.current.scrollLeft;
    const lastScrollLeft = lastScrollLeftRef.current;
    
    // Determine scroll direction
    if (currentScrollLeft > lastScrollLeft) {
      onScrollDirectionChange?.('right');
    } else if (currentScrollLeft < lastScrollLeft) {
      onScrollDirectionChange?.('left');
    }
    
    // Update last scroll position
    lastScrollLeftRef.current = currentScrollLeft;
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Update selection and animate to center after scrolling stops
    scrollTimeoutRef.current = setTimeout(() => {
      const currentSelected = updateSelectedCategory();
      
      // Always animate the selected item to center after scroll stops
      if (currentSelected) {
        setTimeout(() => {
          centerCategory(currentSelected, true); // Use animation
        }, 50);
      }
    }, 500); // Increased from 300ms to 500ms
  };

  // Update selected category based on which item is closest to center
  const updateSelectedCategory = () => {
    if (!scrollContainerRef.current) return null;
    
    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const scrollLeft = container.scrollLeft;
    const itemWidth = 280 + 40; // card width + gap
    const leftPadding = containerWidth / 2 - 140; // Same as paddingLeft
    
    // Find which item is closest to center
    const viewportCenter = scrollLeft + containerWidth / 2;
    let closestIndex = 0;
    let minDistance = Infinity;
    
    for (let i = 0; i < categories.length; i++) {
      const itemLeft = leftPadding + (i * itemWidth);
      const itemCenter = itemLeft + (280 / 2); // center of the card
      const distance = Math.abs(viewportCenter - itemCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }
    
    const newSelectedCategory = categories[closestIndex]?.id;
    if (newSelectedCategory && newSelectedCategory !== selectedCategory) {
      setSelectedCategory(newSelectedCategory);
      onCategorySelect(categories[closestIndex]);
      return newSelectedCategory;
    }
    
    return selectedCategory;
  };

  // Center a specific category
  const centerCategory = (categoryId: string, animated: boolean = false) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const itemWidth = 280 + 40; // card width + gap
    const containerWidth = container.clientWidth;
    const leftPadding = containerWidth / 2 - 140; // Same as paddingLeft
    const index = categories.findIndex(cat => cat.id === categoryId);
    
    if (index >= 0) {
      // Calculate the position to center the item
      const itemLeft = leftPadding + (index * itemWidth);
      const itemCenter = itemLeft + (280 / 2); // center of the card
      const scrollPosition = itemCenter - (containerWidth / 2);
      
      if (animated) {
        // Use smooth animation for clicks
        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      } else {
        // Use instant scroll for programmatic centering
        container.scrollLeft = Math.max(0, scrollPosition);
      }
    }
  };

  // Click handler that centers the clicked category with animation
  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category.id);
    onCategorySelect(category);
    centerCategory(category.id, true); // Use animation for clicks
  };

  // Initialize on mount - center the default selected category
  useEffect(() => {
    if (scrollContainerRef.current && categories.length > 0) {
      // Center the default selected category
      setTimeout(() => {
        centerCategory(selectedCategory);
      }, 100);
    }
  }, [categories.length]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full">
      {/* Heading - centered with max-width */}
      <div className="flex justify-center px-20">
        <p 
          className="font-brand-secondary font-medium text-[32px] leading-[35px] text-center mb-[40px] max-w-[920px]"
          style={{ color: brandConfig.colors.text.primary[isDarkMode ? 'dark' : 'light'] }}
        >
          {getCopy('emptyAccepted.categorySelectionHeading')}
        </p>
      </div>
      
      {/* Simple horizontal scrollable container with touch optimization */}
      <div 
        ref={scrollContainerRef}
        className="flex gap-[40px] overflow-x-auto scrollbar-hide py-4 w-full"
        onScroll={handleScroll}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-x',
          paddingLeft: 'calc(50% - 140px)', // Half container width minus half card width
          paddingRight: 'calc(50% - 140px)', // Half container width minus half card width
        }}
      >
        {categories.map((category) => (
          <motion.div
            key={category.id}
            className="flex-shrink-0 w-[280px] h-[212px] rounded-[24px] border-2 transition-all duration-300 cursor-pointer relative"
            style={{
              borderColor: selectedCategory === category.id 
                ? brandConfig.colors.primary[isDarkMode ? 'dark' : 'light']
                : brandConfig.colors.border[isDarkMode ? 'dark' : 'light'],
              backgroundColor: 'transparent',
              boxShadow: selectedCategory === category.id 
                ? `0 0 0 4px ${brandConfig.colors.primary[isDarkMode ? 'dark' : 'light']}20` 
                : 'none'
            }}
            onClick={() => handleCategoryClick(category)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex flex-col items-center justify-center h-full p-6">
              <div className="flex flex-col gap-2 items-center w-full">
                <div className="flex flex-col gap-3 items-center w-full">
                  <p 
                    className="font-brand-primary font-normal text-[24px] leading-[31px] text-center"
                    style={{ color: brandConfig.colors.text.primary[isDarkMode ? 'dark' : 'light'] }}
                  >
                    {category.name.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < category.name.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                  {category.subtitle && (
                    <p 
                      className="font-brand-body font-light text-[18px] leading-[23px] text-center"
                      style={{ color: brandConfig.colors.text.muted[isDarkMode ? 'dark' : 'light'] }}
                    >
                      {category.subtitle}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default CategorySelector;
